import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  CalendarTimeSlot,
  ClockService,
  groupAvailableBlocks,
  groupByDate,
  groupOverlapping,
  isBlockInPast,
  ITimeSlotTimestamp,
  mapItemsToTimezone,
  ScheduleCalendarAvailableBlockComponent,
  ScheduleCalendarComponent,
  ScheduleCalendarDayContentDirective,
  ScheduleCalendarPeriodBarComponent,
  toUTCTimestamp,
} from '@techspert-io/schedule-calendar';
import { switchMap } from 'rxjs';
import { InterviewPeriodCalendarCallComponent } from '../../../../../features/interview-period/components/calendar-call/interview-period-calendar-call.component';
import { CallSchedulePeriod } from '../../../../../features/interview-period/models/interview-period.models';
import { InterviewPeriodStoreService } from '../../../../../features/interview-period/services/interview-period-store.service';
import { IExpert } from '../../../../../shared/models/expert.interface';
import { TimezonePreferenceService } from '../../../../../shared/services/timezone-preference.service';
import { ExpertAvailabilityBestMatchesComponent } from './best-matches/expert-availability-best-matches.component';
import { findBestMatches } from './best-matches/expert-availability-best-matches.utils';
import {
  ExpertAvailabilityCalendarView,
  ExpertAvailabilityHeaderComponent,
} from './header/expert-availability-header.component';

const PERIOD_AVAILABLE_LABEL = 'Your interview period';
const PERIOD_PREFERRED_LABEL = 'Your preferred interview time';

export interface IExpertAvailabilitySelection {
  range: ITimeSlotTimestamp;
  /** The project's calls the time clashes with */
  overlappingCalls: CallSchedulePeriod[];
}

/**
 * The expert's available times alongside the project's interview period and
 * calls, where a time can be selected from the expert's available times
 */
@Component({
  selector: 'app-expert-availability',
  templateUrl: './expert-availability.component.html',
  styleUrl: './expert-availability.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatProgressBarModule,
    ExpertAvailabilityHeaderComponent,
    ExpertAvailabilityBestMatchesComponent,
    ScheduleCalendarComponent,
    ScheduleCalendarDayContentDirective,
    ScheduleCalendarAvailableBlockComponent,
    ScheduleCalendarPeriodBarComponent,
    InterviewPeriodCalendarCallComponent,
  ],
})
export class ExpertAvailabilityComponent {
  readonly expert = input.required<IExpert>();
  readonly opportunityId = input.required<string>();
  /** Whether a time can be selected from the expert's available times */
  readonly selectable = input(false);
  /** A selected time is being accepted, so no other can be selected */
  readonly selecting = input(false);
  /** A selected time still being checked, kept in its block while loading */
  readonly pendingTime = input<ITimeSlotTimestamp | null>(null);

  readonly selectTime = output<IExpertAvailabilitySelection>();

  private readonly header = viewChild(ExpertAvailabilityHeaderComponent);
  private readonly interviewPeriodStore = inject(InterviewPeriodStoreService);
  private readonly clock = inject(ClockService);

  /** The user's preferred timezone, which the times are shown in */
  readonly timezoneName = inject(TimezonePreferenceService).selectedTimezone;

  readonly interviewPeriod = toSignal(
    toObservable(this.opportunityId).pipe(
      switchMap((opportunityId) =>
        this.interviewPeriodStore.getInterviewPeriod(opportunityId)
      )
    ),
    { initialValue: null }
  );

  readonly periodAvailableLabel = PERIOD_AVAILABLE_LABEL;
  readonly periodPreferredLabel = PERIOD_PREFERRED_LABEL;

  readonly legendItems = [
    { type: 'available', label: 'Expert available' },
    { type: 'preferred', label: 'Expert preferred' },
    { type: 'period-available', label: PERIOD_AVAILABLE_LABEL },
    { type: 'period-preferred', label: PERIOD_PREFERRED_LABEL },
    { type: 'scheduled', label: 'Scheduled call' },
  ];

  readonly isBlockInPast = isBlockInPast;

  /** Times can't be selected without one */
  readonly selectDuration = computed(() =>
    this.selectable() ? this.expert().callDuration || null : null
  );

  readonly dates = computed(() => this.header()?.selectedDates() ?? []);

  /**
   * The selected dates as UTC timestamps, from the start of the first day to
   * the end of the last in the timezone.
   */
  private readonly visibleRange = computed(() => {
    const dates = this.dates();
    const timezoneName = this.timezoneName();

    return dates.length
      ? {
          start: toUTCTimestamp(dates.at(0), '00:00', timezoneName),
          end: toUTCTimestamp(dates.at(-1), '24:00', timezoneName),
        }
      : null;
  });

  private readonly blockList = computed(() => {
    const visibleRange = this.visibleRange();

    return visibleRange
      ? groupAvailableBlocks(
          this.expert().schedule ?? [],
          visibleRange,
          this.timezoneName()
        )
      : [];
  });

  private readonly periodBlockList = computed(() => {
    const visibleRange = this.visibleRange();

    return visibleRange
      ? groupAvailableBlocks(
          this.interviewPeriod()?.available ?? [],
          visibleRange,
          this.timezoneName()
        )
      : [];
  });

  private readonly calls = computed(() => this.interviewPeriod()?.call ?? []);

  private readonly callList = computed(() => {
    const visibleRange = this.visibleRange();

    return visibleRange
      ? mapItemsToTimezone(this.calls(), visibleRange, this.timezoneName())
      : [];
  });

  /** The expert's available blocks by their date */
  readonly blocks = computed(() => groupByDate(this.blockList()));

  /** The interview period's available blocks by their date */
  readonly periodBlocks = computed(() => groupByDate(this.periodBlockList()));

  /** Calls by their date, with overlapping calls grouped to be shown in turn */
  readonly calendarCalls = computed(() =>
    groupByDate(groupOverlapping(this.callList()))
  );

  /**
   * Null when times can't be selected. Kept while a time is being accepted,
   * so the calendar doesn't move.
   */
  readonly bestMatches = computed(() => {
    const duration = this.selectDuration();

    return duration
      ? findBestMatches({
          blocks: this.blockList(),
          duration,
          schedule: this.expert().schedule ?? [],
          interviewPeriod: this.interviewPeriod() ?? {
            available: [],
            call: [],
          },
          now: this.clock.now(),
        })
      : null;
  });

  readonly bestMatchesPeriod = computed(() => {
    const header = this.header();

    if (header?.selectedView() !== ExpertAvailabilityCalendarView.Day) {
      return 'this week';
    }

    return header.isToday() ? 'today' : 'this day';
  });

  /** Whether the expert has times from now on the dates shown */
  readonly hasExpertTimes = computed(() => {
    const now = this.clock.now().getTime();

    return this.blockList().some(({ range }) => Date.parse(range.end) > now);
  });

  readonly expertName = computed(
    () => this.expert().firstName || this.expert().lastName || 'the expert'
  );

  readonly dayItems = computed(() => [
    ...this.blockList(),
    ...this.periodBlockList(),
    ...this.callList(),
  ]);

  /** Emits the time with the project's calls it clashes with */
  onSelectTime({ range }: CalendarTimeSlot) {
    const start = Date.parse(range.start);
    const end = Date.parse(range.end);

    this.selectTime.emit({
      range,
      overlappingCalls: this.calls().filter(
        (call) => Date.parse(call.start) < end && Date.parse(call.end) > start
      ),
    });
  }
}
